import { SEVERITY_TYPES } from '@LSP/constants';
import { ExtractArrayType } from '@prophecy/interfaces/generic';
import { ResultOf } from 'gql.tada';
import { gql } from 'graphql-request';

import { graphql } from '../../graphqlTypes/graphql';

export type DiagnosticsType = {
  severity: SEVERITY_TYPES;
  message: string;
};

export const resetEntitiesQuery = graphql(`
  mutation cleanupGitFiles($projectId: String!, $branchName: String, $resetEntity: [String!]) {
    cleanupGitFiles(projectUID: $projectId, branch: $branchName, resetEntity: $resetEntity) {
      name
    }
  }
`);
const branchInfoQueryWithStaleBranch = graphql(`
  query BranchInfoQuery($projectId: String!, $branchName: String!, $remoteType: RemoteType) {
    BranchInfo(projectUID: $projectId, branchName: $branchName, remoteType: $remoteType) {
      name
      uncommittedFilesCount
      isBranchStale
      remoteCommitInfo {
        commitHash
        message
        created
        authorEmail
        authorName
        tags
      }
      localCommitInfo {
        commitHash
        message
        created
        authorEmail
        authorName
        tags
      }
    }
  }
`);
const branchInfoQuery = graphql(`
  query BranchInfoQuery($projectId: String!, $branchName: String!, $remoteType: RemoteType) {
    BranchInfo(projectUID: $projectId, branchName: $branchName, remoteType: $remoteType) {
      name
      uncommittedFilesCount
      remoteCommitInfo {
        commitHash
        message
        created
        authorEmail
        authorName
        tags
      }
      localCommitInfo {
        commitHash
        message
        created
        authorEmail
        authorName
        tags
      }
    }
  }
`);
type BranchInfoQueryWithStaleBranch = typeof branchInfoQueryWithStaleBranch;
export type BranchInfoQueryType = ResultOf<BranchInfoQueryWithStaleBranch>;
export type CommitInfo = ExtractArrayType<BranchInfoQueryType['BranchInfo']['localCommitInfo']>;
export function getBranchInfoQuery(isBranchStale: boolean) {
  if (isBranchStale) {
    return branchInfoQueryWithStaleBranch;
  }
  return branchInfoQuery as BranchInfoQueryWithStaleBranch;
}
export const gitDiffQuery = graphql(`
  query GitDiffV2($projectId: String!, $branchName: String!) {
    GitDiffV2(projectUID: $projectId, branchName: $branchName) {
      gitDiffOutput
      modifiedFilesContent {
        filePath
        currentContent
        previousContent
      }
    }
  }
`);
export type GitDiffV2QueryType = ResultOf<typeof gitDiffQuery>;

export const branchCommitInfoQuery = graphql(`
  query branchCommitInfoQuery($projectId: String!, $branchName: String!, $remoteType: RemoteType) {
    BranchInfo(projectUID: $projectId, branchName: $branchName, remoteType: $remoteType) {
      uncommittedFiles
      commitLogHeader {
        hasEntityGrouping
        hasEntityLevelReset
        error
      }
      unModifiedEntityInfo {
        uid
        entityKind
        entityName
      }
      uncommittedEntityInfo {
        uid
        entityKind
        entityName
        mutationType
        dependentEntity {
          uid
          entityKind
          entityName
          mutationType
        }
        files
      }
      gitDiffOutput
    }
  }
`);

export const branchCommitInfoQueryOnlyUncommittedEntityInfo = graphql(`
  query branchCommitInfoQueryOnlyUncommittedEntityInfo(
    $projectId: String!
    $branchName: String!
    $remoteType: RemoteType
  ) {
    BranchInfo(projectUID: $projectId, branchName: $branchName, remoteType: $remoteType) {
      uncommittedEntityInfo {
        uid
        entityKind
        entityName
        mutationType
        dependentEntity {
          uid
          entityKind
          entityName
          mutationType
        }
        files
      }
    }
  }
`);

export type BranchCommitInfoQueryType = ResultOf<typeof branchCommitInfoQuery>;
export type UnModifiedEntityInfo = ExtractArrayType<
  NonNullable<BranchCommitInfoQueryType>['BranchInfo']['unModifiedEntityInfo']
>;
export type UncommittedEntityInfo = ExtractArrayType<
  NonNullable<BranchCommitInfoQueryType>['BranchInfo']['uncommittedEntityInfo']
>;

export type DependentEntity = ExtractArrayType<UncommittedEntityInfo['dependentEntity']>;
export type GitActionResponse = ResultOf<typeof commitQuery>['commit'];

const MERGE_CONFLICT_FIELDS = `
  number
  message
  migrationVersion
  mergedPreview {
    conflictsToBeAutogenerated {
      buildFiles
      functionFiles
    }
    fileConflicts {
      conflicts
      noConflicts
      allConflictedFiles
      mergedSqlmRdc
      deletedModifiedConflicts {
          filePath
          deletedOnBranch
          modifiedOnBranch
      }
    }
    entityConflicts {
      entityKind
      entityId
      strategy
      entityName
    }
    projectConflicts {
      key
      displayKey
      leftValue
      rightValue
      strategy
    }
  }
`;
const MERGE_CONFLICT_QUERY = `mergeConflicts {
  ${MERGE_CONFLICT_FIELDS}
}`;
const GitResponseType = `
merged
fromBranch
toBranch
commitHash
fromDisplayBranch
toDisplayBranch
${MERGE_CONFLICT_QUERY}
`;
export const commitQuery = graphql(`
  mutation Commit($projectId: String!, $message: String!, $branch: String) {
    commit(projectUID: $projectId, commitMsg: $message, branch: $branch) {
      ${GitResponseType}
    }
  }
`);

export type GitCommitResponseType = ResultOf<typeof commitQuery>['commit'];

export type GitBranchQueryVariables = {
  projectId: string;
};
export type GitBranchQueryResponse = {
  Project: {
    isExampleProject?: boolean;
    mainBranch: string;
    listBranches: {
      workingBranch: string;
      checkedOutBranches: string[];
    };
  };
};

export const getGitBranchQuery = (remote?: boolean, stale?: boolean) => {
  let name = 'GitBranchQuery';
  let remoteField = '';
  let staleField = '';
  if (remote) {
    name = name + 'WithRemote';
    remoteField = 'remoteBranches';
  }
  if (stale) {
    name = name + 'WithStale';
    staleField = 'staleBranches';
  }
  return gql`
  query ${name}($projectId: String!) {
    Project(uid: $projectId) {
      isExampleProject
      mainBranch
      listBranches {
        workingBranch
        checkedOutBranches
        ${remoteField}
        ${staleField}
      }
    }
  }
`;
};

export const projectWorkingBranchQuery = graphql(`
  query projectWorkingBranchQuery($projectId: String!) {
    Project(uid: $projectId) {
      listBranches {
        workingBranch
      }
    }
  }
`);
export const gitInfoQuery = graphql(`
  query GqlQuery_GitFooter($projectId: String!) {
    Project(uid: $projectId) {
      _id
      name
      industry
      hasAnalysis
      projectType
      mainBranch
      forkMode
      language
      backendLanguage
      externalSubPath
      workingGitContext {
        forkType
        forkMode
        syncMode
        uncommittedFilesCount
        forkNameLabel
      }
    }
  }
`);
export const gitSyncLagInfoQuery = graphql(`
  query GitSyncLagInfoQuery($projectId: String!) {
    Project(uid: $projectId) {
      workingGitContext {
        syncLagInfo {
          remoteName
          behind
          ahead
          warningText
        }
      }
    }
  }
`);

export const createPRQuery = graphql(`
  mutation createPR($projectId: String!, $from: String!, $to: String!, $fromFork: ForkType!, $toFork: ForkType!) {
    createPR(projectUID: $projectId, from: $from, to: $to, fromFork: $fromFork, toFork: $toFork) {
      ${MERGE_CONFLICT_FIELDS}
    }
  }
`);

export const pullUpstreamQuery = graphql(`
  mutation PullUpstream($projectId: String!, $branch: String) {
    pullUpstream(projectUID: $projectId, branch: $branch) {
      ${GitResponseType}
    }
  }
`);

export const pullOriginQuery = graphql(`
  mutation PullOrigin($projectId: String!, $branch: String) {
    pullOrigin(projectUID: $projectId, branch: $branch) {
      ${GitResponseType}
    }
  }
`);

export const cleanCommitQuery = graphql(`
  mutation CleanCommit($projectId: String!, $message: String!) {
    cleanCommit(projectUID: $projectId, commitMsg: $message) {
      ${GitResponseType}
    }
  }
`);

export const checkoutBranchQuery = graphql(`
  mutation checkout($projectId: String!, $branchName: String!) {
    checkout(projectUID: $projectId, branchName: $branchName) {
      name
    }
  }
`);

export const branchDivergenceQuery = graphql(`
  query BranchDivergence(
    $projectId: String!
    $branchName: String!
    $baseBranchName: String!
    $remoteType: RemoteType
    $baseBranchRemoteType: RemoteType
  ) {
    BranchDivergence(
      projectUID: $projectId
      branchName: $branchName
      remoteType: $remoteType
      baseBranchName: $baseBranchName
      baseBranchRemoteType: $baseBranchRemoteType
    ) {
      ahead
      behind
    }
  }
`);
export type BranchDivergenceQueryType = ResultOf<typeof branchDivergenceQuery>;
export const mergePRQuery = graphql(`
  mutation mergeMaster(
    $projectId: String!
    $prNumber: Int!
    $resolvedConflicts: String!
    $entityConflicts: [entityConflicts!]!
    $projectConflicts: [projectConflicts!]!
    $deletedFiles: [String!]!
    $commitMsg: String
    $mergedSqlmRdc: String
  ) {
    mergeMaster(
      projectUID: $projectId
      prNumber: $prNumber
      resolvedConflicts: $resolvedConflicts
      entityConflicts: $entityConflicts
      projectConflicts: $projectConflicts
      deletedFiles: $deletedFiles
      commitMsg: $commitMsg
      mergedSqlmRdc: $mergedSqlmRdc
    ) {
      message
    }
  }
`);

export const releaseProjectQuery = graphql(`
  mutation releaseProject(
    $projectId: String!
    $version: String!
    $message: String!
    $commitHash: String
    $branch: String
    $shouldReleaseCurrentVersion: Boolean
  ) {
    releaseProject(
      uid: $projectId
      releaseVersion: $version
      releaseNotes: $message
      commitHash: $commitHash
      branch: $branch
      shouldReleaseCurrentVersion: $shouldReleaseCurrentVersion
    )
  }
`);

export const isProjectAdminQuery = graphql(`
  query isProjectAdminQuery($projectId: String!) {
    isProjectAdmin(uid: $projectId)
  }
`);

export const deleteBranchQuery = graphql(`
  mutation deleteBranch($projectId: String!, $branchName: String!, $softDeleteBranch: Boolean!) {
    deleteBranch(projectUID: $projectId, branchName: $branchName, softDeleteBranch: $softDeleteBranch)
  }
`);

export const cleanupGitFilesQuery = graphql(`
  mutation cleanupGitFiles($projectId: String!, $branchName: String) {
    cleanupGitFiles(projectUID: $projectId, branch: $branchName) {
      name
    }
  }
`);

export const resetCommitQuery = graphql(`
  mutation resetCommitMutation($projectId: String!, $branchName: String!, $commit: String!) {
    resetCommitWithPullOrigin(projectUID: $projectId, branch: $branchName, commit: $commit){
      ${GitResponseType}
    }
  }
`);

export const versionsQuery = graphql(`
  query Versions($projectId: String!, $excludeMergeCommits: Boolean) {
    Versions(projectUID: $projectId, excludeMergeCommits: $excludeMergeCommits) {
      version
      created
      commitHash
      commits {
        commitHash
        message
        created
        authorEmail
        authorName
        tags
        version
      }
      versionType
      tagMessage
      authorName
      lastDeploymentId
      lastDeploymentTime
      status
      fabrics {
        id
        name
      }
      projectParameterSet
    }
  }
`);

export type VersionsQueryResponse = ResultOf<typeof versionsQuery>;

export const publishVersionQuery = graphql(`
  mutation publishVersion(
    $projectUID: String!,
    $releaseVersion: String!,
    $releaseNotes: String!
  ) {
    publish(
      uid: $projectUID,
      releaseVersion: $releaseVersion,
      releaseNotes: $releaseNotes
    ) {
      merged
      fromBranch
      toBranch
      fromDisplayBranch
      toDisplayBranch
      ${MERGE_CONFLICT_QUERY}
    }
  }
`);

export const syncAllDivergencesQuery = graphql(`
  mutation syncAllDivergences($projectId: String!) {
    syncAllDivergences(projectUID: $projectId) {
      ${GitResponseType}
    }
  }
`);

export const allDivergencesQuery = graphql(`
  query AllDivergences($projectId: String!) {
    AllDivergences(projectUID: $projectId) {
      activeBranchDivergence {
        ahead
        behind
        aheadSubPath
        behindSubPath
      }
      defaultBranchDivergence {
        ahead
        behind
        aheadSubPath
        behindSubPath
      }
      localDivergence {
        ahead
        behind
        aheadSubPath
        behindSubPath
      }
      isSyncRequired
      unCommittedFilesCount
    }
  }
`);

export type AllDivergencesQueryType = ResultOf<typeof allDivergencesQuery>;

export type PublishVersionQueryType = ResultOf<typeof publishVersionQuery>;

export const deployOrchestratorProjectMutation = graphql(`
  mutation deployOrchestratorProject(
    $projectId: String!
    $version: String!
    $fabricIds: [String!]!
    $projectParameterSet: String!
  ) {
    deployOrchestratorProject(
      uid: $projectId
      releaseVersion: $version
      fabricIds: $fabricIds
      projectParameterSet: $projectParameterSet
    )
  }
`);

export type DeployOrchestratorProjectMutationType = ResultOf<typeof deployOrchestratorProjectMutation>;

export const commitDiffQuery = graphql(`
  query GitDiff($projectId: String!, $currentCommit: String!, $previousCommit: String!, $branchName: String!) {
    GitDiff(
      projectUID: $projectId
      currentCommit: $currentCommit
      previousCommit: $previousCommit
      branchName: $branchName
    ) {
      gitDiffOutput
      modifiedFiles
      modifiedEntities {
        uid
        entityKind
        entityName
        mutationType
        files
      }
    }
  }
`);

export type GitDiffResponse = {
  gitDiffOutput: string;
  modifiedFiles: never[];
  modifiedEntities: {
    uid: string;
    entityKind: string;
    entityName: string;
    mutationType: string;
    files: never[];
  }[];
};

export type ModifiedEntity = {
  uid: string;
  entityKind: string;
  entityName: string;
  mutationType: string;
};

export type CommitDiffQueryType = ResultOf<typeof commitDiffQuery>;

export type VersionsQueryVariables = {
  projectId: string;
  excludeMergeCommits?: boolean;
};

export const bumpVersionSuggestionsQuery = graphql(`
  query bumpVersionSuggestions($projectId: String!, $branch: String) {
    bumpVersionSuggestions(uid: $projectId, branch: $branch)
  }
`);

export type BumpVersionSuggestionsQueryType = ResultOf<typeof bumpVersionSuggestionsQuery>;

export const bumpVersionProjectQuery = graphql(`
  mutation bumpVersionProject($projectId: String!, $branch: String!, $bumpVersion: String!) {
    bumpVersionProject(uid: $projectId, branch: $branch, bumpVersion: $bumpVersion)
  }
`);

export const latestVersionDetailQuery = graphql(`
  query latestVersionDetail($projectId: String!, $branch: String) {
    latestVersionDetail(uid: $projectId, branch: $branch) {
      latestVersion
      isReleased
    }
  }
`);
